import React, { useState } from 'react';
import { Button, Empty, Flex, message, Modal, Select, Skeleton, Table, Tag, Typography } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';

import { Lineitem } from '../../../../interfaces/Ordel.Interface';
import { CleaningRequirement } from '../../../../interfaces/ProcessCatalog.Interface';
import {
	useGetAllDeliveryByOrderItemQuery,
	useGetBatchSiblingsQuery,
	usePrintDeliveryByIdMutation,
	useReassignDeliveryMutation,
} from '../../../../redux/api/order-line-item.ts';
import { usePrintLocation } from '../../../../context/PrintLocationContext';
import useIsShopManager from '../../../../hooks/useIsShopManager';
import { confirmCleaning } from '../../../ProcessCatalog/confirmCleaning';

const { Text } = Typography;

interface IProps {
	item: Lineitem;
	showReprint?: boolean;
	onChanged?: () => void;
}

const deliveryStatusColor = (status?: string) => {
	if (status === 'Delivered in Full') return 'green';
	if (status === 'Partial Delivery') return 'orange';
	return 'default';
};

const DeliveryList: React.FC<IProps> = ({ item, showReprint = false, onChanged }: IProps) => {
	const { data, error, isFetching, isLoading, refetch } = useGetAllDeliveryByOrderItemQuery({
		id: item.id,
	});
	const [printDelivery] = usePrintDeliveryByIdMutation();
	const { promptForBuilding } = usePrintLocation();
	const { isShopManager } = useIsShopManager();
	const { data: siblingData } = useGetBatchSiblingsQuery(
		{ id: item.id },
		{ skip: !isShopManager }
	);
	const [reassignDelivery, { isLoading: isMoving }] = useReassignDeliveryMutation();
	const [movingId, setMovingId] = useState<string | null>(null);
	const [targetId, setTargetId] = useState<string | null>(null);
	const siblings = siblingData?.siblings || [];

	React.useEffect(() => {
		refetch();
	}, []);

	if (!data) {
		return <Skeleton />;
	}

	if (error) {
		return <div>Error loading data</div>;
	}

	const columns = [
		{
			title: 'Date',
			dataIndex: 'created_time',
			key: 'date',
			width: 170,
			render: (val: string) => (
				<Text style={{ fontSize: 13 }}>
					{new Date(val).toLocaleDateString()} {new Date(val).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
				</Text>
			),
		},
		{
			title: 'By',
			dataIndex: 'created_by_name',
			key: 'created_by',
			width: 140,
			render: (val: string) => (
				<Text style={{ fontSize: 13 }}>{val || '—'}</Text>
			),
		},
		{
			title: 'Delivered',
			dataIndex: 'delivered_quantity',
			key: 'delivered',
			width: 90,
			align: 'center' as const,
			render: (val: number) => <Text strong>{val ?? 0}</Text>,
		},
		{
			title: 'Remaining',
			dataIndex: 'remaining_quantity',
			key: 'remaining',
			width: 90,
			align: 'center' as const,
			render: (val: number) => <Text>{val ?? 0}</Text>,
		},
		{
			title: 'Returned',
			dataIndex: 'returned_quantity',
			key: 'returned',
			width: 90,
			align: 'center' as const,
			render: (val: number) => {
				const v = val ?? 0;
				return <Text type={v > 0 ? 'warning' : undefined}>{v}</Text>;
			},
		},
		{
			title: 'Scrapped',
			dataIndex: 'scrapped_quantity',
			key: 'scrapped',
			width: 90,
			align: 'center' as const,
			render: (val: number) => {
				const v = val ?? 0;
				return <Text type={v > 0 ? 'danger' : undefined}>{v}</Text>;
			},
		},
		...(isShopManager && siblings.length > 0
			? [
					{
						title: '',
						key: 'move',
						width: 80,
						align: 'center' as const,
						render: (_: unknown, record: { id: string; delivered_quantity?: number }) =>
							(record.delivered_quantity ?? 0) > 0 ? (
								<Button size="small" onClick={() => {
									setTargetId(null);
									setMovingId(record.id);
								}}>
									Move
								</Button>
							) : null,
					},
				]
			: []),
		...(showReprint ? [{
			title: '',
			key: 'actions',
			width: 70,
			align: 'center' as const,
			render: (_: any, record: any) => (
				<Button
					size="small"
					icon={<PrinterOutlined />}
					onClick={() => {
						promptForBuilding(
							async (selectedBuildingId) => {
								try {
									await printDelivery({
										order_item_id: item.id,
										delivery_id: record.id,
										...(selectedBuildingId
											? { building_id: selectedBuildingId }
											: {}),
									}).unwrap();
									message.success('Re-print sent');
								} catch {
									message.error('Re-print failed');
								}
							},
							{ description: 'Select the building to reprint the delivery tag at.' },
						);
					}}
				>
					Re-Print
				</Button>
			),
		}] : []),
	];

	const totalDelivered = item.delivered_quantity ?? 0;

	return (
		<div>
			<Flex gap={16} className="mb-3" align="center">
				<Text type="secondary" style={{ fontSize: 13 }}>
					<strong>{totalDelivered}</strong> of <strong>{item.quantity}</strong> delivered
				</Text>
				{data.truncated && (
					<Text type="warning" style={{ fontSize: 13 }}>
						Showing the first {data.results.length} of {data.count} deliveries.
					</Text>
				)}
				{item.delivery_status && item.delivery_status !== 'Not Started' && (
					<Tag color={deliveryStatusColor(item.delivery_status)}>
						{item.delivery_status}
					</Tag>
				)}
			</Flex>

			<Table
				size="small"
				dataSource={data?.results || []}
				columns={columns}
				loading={isLoading || isFetching}
				rowKey="id"
				pagination={false}
				scroll={{ x: 'max-content' }}
				locale={{ emptyText: <Empty description="No deliveries recorded yet" /> }}
			/>
			<Modal
				title="Move delivery to another job"
				open={movingId !== null}
				okText="Move"
				okButtonProps={{ disabled: !targetId, loading: isMoving }}
				onCancel={() => setMovingId(null)}
				onOk={async () => {
					if (!movingId || !targetId) return;
					const deliveryId = movingId;
					const target = targetId;

					const submitMove = async (cleaningAcknowledged?: boolean) => {
						try {
							await reassignDelivery({
								id: item.id,
								delivery_id: deliveryId,
								target_line_item_id: target,
								...(cleaningAcknowledged
									? { cleaning_instructions_acknowledged: true }
									: {}),
							}).unwrap();
							message.success('Delivery moved');
							setMovingId(null);
							refetch();
							onChanged?.();
						} catch (err) {
							const data = (
								err as {
									data?: {
										message?: string;
										cleaning_confirmation_required?: boolean;
										cleaning?: CleaningRequirement;
									};
								}
							)?.data;
							if (
								!cleaningAcknowledged &&
								data?.cleaning_confirmation_required &&
								data.cleaning
							) {
								const cleaned = await confirmCleaning(data.cleaning);
								if (!cleaned) {
									message.warning(
										'Move cancelled. Clean the parts as required, then try again.',
									);
									return;
								}
								await submitMove(true);
								return;
							}
							message.error(data?.message || 'Could not move the delivery');
						}
					};

					await submitMove();
				}}
			>
				<Text>
					These pieces will count on the job you pick. Intake in PartsBadger is not moved with them.
				</Text>
				<Select
					style={{ width: '100%', marginTop: 12 }}
					placeholder="Job"
					value={targetId}
					onChange={setTargetId}
					options={siblings
						.filter((sibling) => sibling.remaining_quantity > 0)
						.map((sibling) => ({
							value: sibling.id,
							label: `${sibling.required_partner_ship_date || sibling.required_part_ship_date || 'no ship date'} — ${sibling.remaining_quantity} left of ${sibling.quantity}`,
						}))}
				/>
			</Modal>
		</div>
	);
};

export default DeliveryList;
